import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { dictionaryApi } from '../../api/endpoints';
import DataTable from '../../components/DataTable';
import useAdminList from '../../hooks/useAdminList';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

/**
 * Одна страница на все семь справочников.
 *
 * Раньше под каждый был свой файл-близнец (AddAuthors, AddKeyword,
 * AddAuthorsEducational, AddCategoriesEducational, AddAuthorsDisertation,
 * AddNapravlenieDisertation, AddSpecDisertation) — около тысячи строк почти
 * одинакового кода.
 *
 * Заодно исправлено то, что поле ввода работало и как фильтр, и как поле нового
 * значения: набрал текст для поиска, нажал «Добавить» — и создалась запись.
 * Здесь это два разных поля.
 */

const CONFIG = {
    authors: { title: 'Авторы статей', item: 'автора', usedIn: 'статьях' },
    keywords: { title: 'Ключевые слова', item: 'ключевое слово', usedIn: 'статьях' },
    'ed-authors': { title: 'Авторы учебных изданий', item: 'автора', usedIn: 'изданиях' },
    'ed-categories': { title: 'Направления учебных изданий', item: 'направление', usedIn: 'изданиях' },
    'dis-authors': { title: 'Авторы диссертаций', item: 'автора', usedIn: 'диссертациях' },
    'dis-napravlenie': { title: 'Направления диссертаций', item: 'направление', usedIn: 'специальностях' },
    'dis-spec': {
        title: 'Специальности',
        item: 'специальность',
        usedIn: 'диссертациях',
        parent: { type: 'dis-napravlenie', label: 'Направление' },
    },
};

const DictionaryPage = () => {
    const { type } = useParams();
    const config = CONFIG[type];
    const { toast, confirm } = useUi();

    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');
    const [parentOptions, setParentOptions] = useState([]);
    const [parent, setParent] = useState('');
    const [editing, setEditing] = useState(null); // { id, name }

    const fetcher = useCallback((params) => dictionaryApi.list(type, params), [type]);
    const list = useAdminList(fetcher, { sort: 'name', dir: 'asc' });
    const { reload, setFilter } = list;

    // Сбрасывать состояние при смене type здесь не нужно: маршрут пересоздаёт
    // компонент через key={type}, см. AdminRoutes.

    // Специальности принадлежат направлению — подгружаем список направлений.
    useEffect(() => {
        if (!config || !config.parent) {
            setParentOptions([]);
            return undefined;
        }

        let cancelled = false;
        dictionaryApi
            .options(config.parent.type)
            .then((items) => !cancelled && setParentOptions(items))
            .catch((e) => !cancelled && toast.error(e.message));

        return () => {
            cancelled = true;
        };
    }, [config, toast]);

    useEffect(() => {
        if (config && config.parent) {
            setFilter('parent', parent || '');
        }
    }, [parent, config, setFilter]);

    if (!config) {
        return (
            <div className="adm-card adm-card-body">
                <p>Неизвестный справочник: {type}</p>
            </div>
        );
    }

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateError('');

        const name = newName.trim();
        if (!name) {
            setCreateError('Введите название.');
            return;
        }
        if (config.parent && !parent) {
            setCreateError(`Сначала выберите поле «${config.parent.label}».`);
            return;
        }

        setCreating(true);
        try {
            const payload = { name };
            if (config.parent) payload.parent = parent;

            await dictionaryApi.create(type, payload);
            toast.success('Запись добавлена');
            setNewName('');
            reload();
        } catch (err) {
            setCreateError(err.fieldError('name') || err.message);
        } finally {
            setCreating(false);
        }
    };

    const handleSaveEdit = async () => {
        const name = editing.name.trim();
        if (!name) return;

        try {
            await dictionaryApi.update(type, editing.id, { name });
            toast.success('Сохранено');
            setEditing(null);
            reload();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDelete = async (row) => {
        const inUse = row.usage > 0;

        const ok = await confirm({
            title: `Удалить ${config.item}?`,
            message: inUse
                ? `«${row.name}»\n\nЗапись используется в ${row.usage} ${config.usedIn}. После удаления связь в них останется битой.`
                : `«${row.name}»\n\nЗапись нигде не используется.`,
            confirmLabel: 'Удалить',
            danger: true,
        });
        if (!ok) return;

        try {
            // force нужен, когда на запись ещё есть ссылки: без него бэкенд вернёт 409.
            await dictionaryApi.remove(type, row.id, { force: inUse });
            toast.success('Запись удалена');
            reload();
        } catch (err) {
            if (err.isConflict) {
                const forceOk = await confirm({
                    title: 'Всё равно удалить?',
                    message: err.message,
                    confirmLabel: 'Удалить',
                    danger: true,
                });
                if (!forceOk) return;
                try {
                    await dictionaryApi.remove(type, row.id, { force: true });
                    toast.success('Запись удалена');
                    reload();
                } catch (e2) {
                    toast.error(e2.message);
                }
                return;
            }
            toast.error(err.message);
        }
    };

    const columns = [
        { key: 'id', title: 'ID', width: 80, sortable: true },
        {
            key: 'name',
            title: 'Название',
            sortable: true,
            render: (row) =>
                editing && editing.id === row.id ? (
                    <div className={styles.editRow}>
                        <input
                            className="adm-input"
                            value={editing.name}
                            autoFocus
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEdit();
                                if (e.key === 'Escape') setEditing(null);
                            }}
                        />
                        <button type="button" className="adm-btn adm-btn-primary adm-btn-sm" onClick={handleSaveEdit}>
                            Сохранить
                        </button>
                        <button
                            type="button"
                            className="adm-btn adm-btn-ghost adm-btn-sm"
                            onClick={() => setEditing(null)}
                        >
                            Отмена
                        </button>
                    </div>
                ) : (
                    row.name
                ),
        },
        {
            key: 'usage',
            title: 'Использований',
            width: 150,
            render: (row) =>
                row.usage > 0 ? (
                    <span className={styles.usageBadge}>
                        {row.usage} в {config.usedIn}
                    </span>
                ) : (
                    <span className="adm-muted">—</span>
                ),
        },
        {
            key: 'actions',
            title: '',
            width: 150,
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button
                        type="button"
                        className="adm-btn adm-btn-ghost adm-btn-sm"
                        onClick={() => setEditing({ id: row.id, name: row.name })}
                    >
                        Изменить
                    </button>
                    <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => handleDelete(row)}
                    >
                        Удалить
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">{config.title}</h1>
                    <p className="adm-page-subtitle">
                        Записи связаны с основными разделами — перед удалением видно, где они используются
                    </p>
                </div>
            </div>

            <div className="adm-card" style={{ marginBottom: 20 }}>
                <div className="adm-card-header">
                    <h2 className="adm-card-title">Добавить запись</h2>
                </div>
                <div className="adm-card-body">
                    <form className={styles.inlineForm} onSubmit={handleCreate}>
                        {config.parent && (
                            <select
                                className={styles.filterSelect}
                                value={parent}
                                onChange={(e) => setParent(e.target.value)}
                                aria-label={config.parent.label}
                            >
                                <option value="">— {config.parent.label.toLowerCase()} —</option>
                                {parentOptions.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}
                                    </option>
                                ))}
                            </select>
                        )}

                        <div className={styles.inlineFormInput}>
                            <input
                                className={`adm-input ${createError ? 'adm-input-invalid' : ''}`}
                                value={newName}
                                onChange={(e) => {
                                    setNewName(e.target.value);
                                    setCreateError('');
                                }}
                                placeholder="Название новой записи"
                                aria-label="Название новой записи"
                            />
                            {createError && <div className="adm-error">{createError}</div>}
                        </div>

                        <button type="submit" className="adm-btn adm-btn-primary" disabled={creating}>
                            {creating ? 'Добавление…' : 'Добавить'}
                        </button>
                    </form>
                </div>
            </div>

            <DataTable
                columns={columns}
                rows={list.rows}
                loading={list.loading}
                error={list.error}
                onRetry={list.reload}
                search={list.search}
                onSearchChange={list.setSearch}
                searchPlaceholder="Поиск по названию"
                sort={list.sort}
                dir={list.dir}
                onSortChange={list.toggleSort}
                page={list.page}
                perPage={list.perPage}
                total={list.total}
                lastPage={list.lastPage}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
                emptyText={list.search ? 'По запросу ничего не найдено' : 'Записей пока нет'}
                filters={
                    config.parent && (
                        <select
                            className={styles.filterSelect}
                            value={parent}
                            onChange={(e) => setParent(e.target.value)}
                            aria-label={`Фильтр: ${config.parent.label}`}
                        >
                            <option value="">Все направления</option>
                            {parentOptions.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    )
                }
            />
        </>
    );
};

export default DictionaryPage;
